import { useState, useEffect, useId } from "react";
import { Box, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
// import logo from "../../../../assets/icons/landing/logo.svg";
import {
  primaryAlt,
  textPrimary,
  textSecondary,
  white,
} from "../../../../utils/colors";
// import LanguageButtonMain from "../../../../components/buttons/LanguageButtonMain";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PatientPortalVerifyAccess from "./partials/PatientPortalVerifyAccess";
import { useTranslation } from "react-i18next";
import PatientPortalClinicInfo from "./partials/PatientPortalClinicInfo";
import LanguageButtonMain from "../../../../components/buttons/LanguageButtonMain";
import { jwtDecode } from "jwt-decode";
import PatientPortalVerified from "./partials/PatientPortalVerified";
import {
  getPublicPortalPatientDetailsAction,
  publicVerifyPatientPortalPinAction,
} from "../../../../actions/publicActions";
import { PUBLIC_PORTAL_PATIENT_VERIFY_PIN_RESET } from "../../../../constants/publicConstants";
import { Helmet } from "react-helmet";
import useIsMobile from "../../../../hooks/useIsMobile";

const PatientPortalPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const isMobile = useIsMobile();

  // ID
  const { slug } = useParams();
  const { patientId } = useParams();

  // STATE
  //   const [isPinVerified, setIsPinVerified] = useState(false);
  const [pinCode, setPinCode] = useState("");

  // REDUX
  const dispatch = useDispatch();

  const publicPortalPatientDetails = useSelector(
    (state) => state.publicPortalPatientDetails
  );
  const {
    loading: loadingPatientPortal,
    error: errorPatientPortal,
    patientPortalPublicDetails,
  } = publicPortalPatientDetails;

  const publicPortalPatientVerifyPin = useSelector(
    (state) => state.publicPortalPatientVerifyPin
  );
  const {
    loading: loadingVerifyPin,
    error: errorVerifyPin,
    success: successVerifyPin,
    patientToken,
  } = publicPortalPatientVerifyPin;

  // Scoped localStorage key
  const localStorageKey = `patientInfo-${slug}-${patientId}`;
  const storedPatientInfo = localStorage.getItem(localStorageKey);

  const patientInfo =
    patientToken || (storedPatientInfo && JSON.parse(storedPatientInfo));

  const isVerified =
    !!patientInfo?.token &&
    jwtDecode(patientInfo.token)?.patientId === patientId;

  // HANDLERS
  const verifyPinHandler = (pin) => {
    dispatch(publicVerifyPatientPortalPinAction(slug, patientId, pin));
  };

  // USE EFFECT

  useEffect(() => {
    const keys = Object.keys(localStorage);

    keys.forEach((key) => {
      // Remove old-style keys
      if (key === "patientInfo") {
        localStorage.removeItem(key);
      }

      // Remove other patients' tokens
      if (key.startsWith("patientInfo-") && key !== localStorageKey) {
        localStorage.removeItem(key);
      }
    });

    if (storedPatientInfo) {
      try {
        const { token } = JSON.parse(storedPatientInfo);
        const decoded = jwtDecode(token);
        const isExpired = decoded.exp * 1000 < Date.now() + 5000; // 5 second buffer
        const isWrongPatient = decoded.patientId !== patientId;

        if (isExpired || isWrongPatient) {
          localStorage.removeItem(localStorageKey);
          dispatch({ type: PUBLIC_PORTAL_PATIENT_VERIFY_PIN_RESET });

          toast({
            title: t("toasts.portalSessionExpired"),
            status: "info",
            duration: 4000,
            isClosable: true,
          });
        }
      } catch (err) {
        console.error("Invalid token", err);
        localStorage.removeItem(localStorageKey);
        dispatch({ type: PUBLIC_PORTAL_PATIENT_VERIFY_PIN_RESET });
      }
    }
  }, [slug, patientId, dispatch]);

  useEffect(() => {
    if (successVerifyPin && patientToken?.token) {
      localStorage.setItem(localStorageKey, JSON.stringify(patientToken));

      // 🔥 REMOVE legacy key immediately to avoid duplicates
      if (localStorage.getItem("patientInfo")) {
        localStorage.removeItem("patientInfo");
      }
    }
  }, [successVerifyPin, patientToken]);

  useEffect(() => {
    dispatch(getPublicPortalPatientDetailsAction(slug, patientId));
  }, [dispatch]);

  useEffect(() => {
    if (errorVerifyPin) {
      toast({
        title: t("toasts.incorrectPinCode"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [errorVerifyPin]);

  // LOG
  // console.log("🚀 ~ file: PatientPortal.js ~ line 52 ~ tenantID", tenantID);
  // console.log("🚀 ~ file: PatientPortal.js ~ line 52 ~ patientId", patientId);
  // console.log("🚀 ~ file: PatientPortal.js ~", patientPortalPublicDetails);
  // console.log("🚀 ~ file: PatientPortal.js ~", patientToken);

  return (
    <>
      <Helmet>
        <title>{t("pageTitle.patientPortal")}</title>
        <meta name="description" content={t("pageDescription.patientPortal")} />
      </Helmet>

      {!loadingPatientPortal ? (
        <Box w="100%" bg={white}>
          {/* TOP START */}
          <Box w="100%" bg="#f8f8f8" position="relative">
            {/* TOP LEFT */}
            <Box
              w="100%"
              maxW={["100%", "1200px"]}
              m="0 auto"
              p="2rem 2rem 0.5rem"
            >
              <Flex w="100%" justify="space-between">
                <Flex w={["initial", "50%"]} direction="column">
                  <PatientPortalClinicInfo
                    loadingPatientPortal={loadingPatientPortal}
                    patientPortalPublicDetails={patientPortalPublicDetails}
                  />
                </Flex>
                <Flex
                  w={["initial", "50%"]}
                  align="flex-start"
                  justify="flex-end"
                >
                  <LanguageButtonMain />
                </Flex>
              </Flex>

              <Flex align="center" justify="space-between" m="4rem 0 0">
                <Flex w={["100%", "50%"]}>
                  {patientPortalPublicDetails && (
                    <Text
                      fontSize={["1.5rem", "2.25rem"]}
                      fontWeight="300"
                      color={textSecondary}
                    >
                      {t("patient.patientPortalGreeting")}
                      <Text
                        as="span"
                        fontWeight="500"
                        color={textPrimary}
                        ml={1}
                      >
                        {patientPortalPublicDetails.firstName}
                      </Text>
                    </Text>
                  )}
                </Flex>
                <Flex w="50%"></Flex>
              </Flex>
            </Box>
          </Box>
          {/* TOP END */}

          {!loadingVerifyPin || !loadingPatientPortal ? (
            <Box>
              {isVerified ? (
                <Box w="100%">
                  <PatientPortalVerified
                    slug={slug}
                    patientId={patientId}
                    clinicName={patientPortalPublicDetails?.clinicName}
                    token={patientInfo?.token}
                    patientPortalPublicDetails={patientPortalPublicDetails}
                  />
                </Box>
              ) : (
                // ENTER PIN START
                <Box w="100%" p={["3rem 1.5rem 0", "3rem 0 0"]}>
                  <Flex
                    w="100%"
                    align="center"
                    justify="center"
                    direction={["column", "column"]}
                  >
                    <Text
                      fontSize={["1.5rem", "2rem"]}
                      color={textPrimary}
                      fontWeight="500"
                    >
                      {t("patient.patientPortalTitle")}
                      {isMobile && (
                        <Text
                          as="span"
                          fontSize={["1.5rem", "2rem"]}
                          color={primaryAlt}
                          fontWeight="500"
                          textAlign="center"
                          ml={1}
                        >
                          {patientPortalPublicDetails?.clinicName}
                        </Text>
                      )}
                    </Text>
                    {!isMobile && (
                      <Text
                        fontSize={["1.5rem", "2rem"]}
                        color={primaryAlt}
                        fontWeight="500"
                        textAlign="center"
                      >
                        {patientPortalPublicDetails?.clinicName}
                      </Text>
                    )}
                  </Flex>

                  {/* VERIFICATION */}
                  <PatientPortalVerifyAccess
                    onVerify={verifyPinHandler}
                    pinCode={pinCode}
                    setPinCode={setPinCode}
                    email={patientPortalPublicDetails?.maskedEmail}
                  />
                </Box>
                // ENTER PIN END
              )}

              {/* LOGOUT TEMPORARY */}
              {/* {isVerified && !loadingVerifyPin && (
                <Button
                  onClick={() => {
                    localStorage.removeItem(localStorageKey);
                    dispatch(logoutPatientPortalAction());
                  }}
                  colorScheme="red"
                  size="sm"
                >
                  Logout
                </Button>
              )} */}
            </Box>
          ) : (
            <Flex w="100%" align="center" justify="center" h="400px">
              <Spinner size="lg" color={primaryAlt} />
            </Flex>
          )}
        </Box>
      ) : (
        <Flex w="100%" align="center" justify="center" h="100vh">
          <Spinner size="lg" color={primaryAlt} />
        </Flex>
      )}
    </>
  );
};

export default PatientPortalPage;
